import { useRef, useState, useEffect } from "react";
import { Sparkles, X, Send, CornerDownLeft } from "lucide-react";
import { API } from "@/lib/api";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Which residents need attention today?",
  "Why did our compliance score decline?",
  "Which staff certifications expire this month?",
  "Which programs have staffing shortages?",
  "Generate a manager briefing.",
];

export default function CopilotDrawer({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/copilot`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.session_id) sessionRef.current = data.session_id;
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "⚠️ " + data.error };
                return copy;
              });
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Unable to reach the AI service." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <div onClick={onClose}
        className={cn("fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0")} />
      <div data-testid="copilot-drawer"
        className={cn("fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l bg-card shadow-2xl transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full")}>
        <div className="flex items-center justify-between border-b px-5 py-4"
          style={{ background: "linear-gradient(90deg,#7C3AED10,transparent)" }}>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#7C3AED] text-white">
              <Sparkles size={18} />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">CAREIQ Copilot</div>
              <div className="text-[11px] text-muted-foreground">Decision support · human-reviewed</div>
            </div>
          </div>
          <button data-testid="copilot-close-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        <div ref={scrollRef} className="careiq-scroll flex-1 space-y-4 overflow-y-auto p-5">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="rounded-xl border bg-background p-4 text-sm text-muted-foreground">
                Ask about residents, medications, staffing, compliance and more. Answers are grounded in your
                organization's data. I provide decision support only — clinical and high-impact decisions stay with your team.
              </div>
              <div className="space-y-1.5">
                <div className="overline">Try asking</div>
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`copilot-suggestion-${i}`} onClick={() => send(s)}
                    className="flex w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-left text-[13px] transition-colors hover:border-[#7C3AED] hover:bg-[#7C3AED]/5">
                    <Sparkles size={13} className="text-[#7C3AED]" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[85%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed",
                m.role === "user" ? "bg-[#155EEF] text-white" : "border bg-background")}>
                {m.content || (streaming && i === messages.length - 1 ? <span className="careiq-pulse">Thinking…</span> : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t p-3">
          <div className="flex items-end gap-2 rounded-lg border bg-background p-2 focus-within:border-[#7C3AED]">
            <textarea data-testid="copilot-input" rows={1} value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask CAREIQ Copilot…"
              className="max-h-28 flex-1 resize-none bg-transparent px-1 text-sm outline-none" />
            <button data-testid="copilot-send-btn" onClick={() => send()} disabled={streaming || !input.trim()}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-[#7C3AED] text-white transition-opacity disabled:opacity-40">
              <Send size={15} />
            </button>
          </div>
          <div className="mt-1.5 flex items-center gap-1 px-1 text-[10px] text-muted-foreground">
            <CornerDownLeft size={11} /> Enter to send · Shift+Enter for newline
          </div>
        </div>
      </div>
    </>
  );
}
